import { Reducer, useReducer, useEffect, useMemo, useState, Dispatch } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSteps, MainContentPaddingTypes, NewModal, NewSectionModalLvl1, NewSectionModalLvl2, NewSectionModalLvl3, Section, WaitAnimation } from "../..";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { Graph_QL, useSection, useTerminalTypes } from "../../../Services";
import { useAppSelector } from "../../../Store";
import { DirectionOptions, NumberingOptions, PatternOptions, Pen, StartCornerOptions, Station } from "../../../Definitions/newSectionModalInterfaces";
import { SystemTypes } from "../../../Definitions";
import NewEsfWfSectionModalLvl1 from "../../EsfWalkThrough/Modals/newEsfWfSectionModalLvl1";
import NewEsfWfSectionModalLvl2 from "../../EsfWalkThrough/Modals/newEsfWfSectionModalLvl2";
import NewEsfWfSectionModalLvl3 from "../../EsfWalkThrough/Modals/newEsfWfSectionModalLvl3";
import { CreateTerminalLink } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/createLfSectionCall";
import { lfExtractTerminalLinks, transformData } from "./_Modals/newSectionModalHelpers";
import { IModalContext, ModalContext, ModalDispatcherT, ModalReducer } from "./_Modals/newSectionModalContext";
import { DevicePortsUsed, dispatchTerminalConflict } from "./_Modals/dispatchTerminalConflict";
import { useLazyQuery } from "@apollo/client";
import { VERIFY_SECTION_CREATE, VerifySectionCreateResponse } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/verifySectionCreateCall";
import NewButton from "../../BaseComponents/newButton";



interface NewSectionModal_Props {
  closeModal: () => void,
  systemId: number;
  systemType: SystemTypes
}

export default function NewSectionModal({ closeModal, systemId, systemType }: NewSectionModal_Props) {
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const {domain, connection} = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm })
    let connection = Graph_QL.create({ domain: farm })
    return {domain, connection}
  }, [farm]);
  const [loading, setLoading] = useState(false);
  
  const [_state, dispatch] = useReducer<Reducer<IModalContext, ModalDispatcherT>>(ModalReducer, {
    farm: farm,
    domain: domain,
    connection: connection,
    currentSectionLevel: 1,
    section: {
      name: "",
      contents: [],
      feeder: 1,
      lanController: "",
      columns: systemType === SystemTypes.freedaLf ? 2 : systemType === SystemTypes.freedaEsf_WalkThrough ? 1 : 2,
      rows: 5,
      startNumber: 101,
      numbering: NumberingOptions.manual,
      startCorner: StartCornerOptions.topLeft,
      direction: DirectionOptions.horizontal,
      pattern: PatternOptions.z
    } as Section,
    systemType: systemType,
    error: { Elementid: 0, errorText: "" }
  } as IModalContext)

  const { t } = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  
  const  [verifyNewTerminals,] = useLazyQuery<VerifySectionCreateResponse>(VERIFY_SECTION_CREATE, {fetchPolicy: "no-cache"})
  //variables: {domain: farm, section: suggested}, 
  const { createSectionLocations } = useSection(connection);
  const { data: terminalTypesState, call: getTerminalTypes } = useTerminalTypes(connection);
  
  useEffect(() => {
    if (!terminalTypesState[domain]) {
      getTerminalTypes(undefined)
    }
  }, [domain, getTerminalTypes, terminalTypesState])
  
  const handleSaveSection = async () => {
    const handleTimeout = (elementId: number) => {
      setTimeout(() => {
        dispatch({ error: { Elementid: elementId, errorText: "" } });
      }, 2000)
    }
    //GoNext
    if (_state.currentSectionLevel === 1) {
      if (!_state.section.name) {
        return (
          dispatch({ currentSectionLevel: 1, error: { Elementid: 1, errorText: "Missing name" } }),
          handleTimeout(1)
        )
      }
      if (!_state.section.contents.length) {
        return (
          dispatch({ currentSectionLevel: 1, error: { Elementid: 2, errorText: "Missing pens" } }),
          handleTimeout(2)
        )
      }
      return dispatch({ currentSectionLevel: _state.currentSectionLevel + 1 })
    }
    
    const newSectionData = transformData(_state.section.contents, _state.section, _state.systemType)
    
    if (_state.currentSectionLevel === 2) { 
      if (_state.section.contents.some((pen) => !pen.io)) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 3, errorText: "Missing I/O Board" } }),
          handleTimeout(3)
        )
      }
      if (_state.section.contents.some((pen) => !pen.feederOutput)) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 4, errorText: "Missing feeder output" } }),
          handleTimeout(4)
        )
      }
      if (_state.systemType === SystemTypes.freedaLf && _state.section.water && _state.section.contents.some((pen) => (pen as Pen).waterOutput === undefined)) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 5, errorText: "Missing water output" } }),
          handleTimeout(5)
        )
      }
      if (_state.systemType === SystemTypes.freedaLf && _state.section.activator && _state.section.contents.some((pen) => (pen as Pen).activatorInput === undefined)) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 6, errorText: "Missing activator input" } }),
          handleTimeout(6)
        )
      }
      setLoading(true);
      const verifyCreate = await verifyNewTerminals({variables: {domain: farm, section: newSectionData}})
      
      const devicePortsUsed = verifyCreate.data?.section.verifyCreate.existingTerminals.reduce((accumulator, terminal) => {
        if (accumulator[terminal.device_id] === undefined) {
          accumulator[terminal.device_id] = [];
        }
        accumulator[terminal.device_id].push(terminal.port);
        return accumulator}
      ,{} as DevicePortsUsed)
      
      setLoading(false);
      if (dispatchTerminalConflict(dispatch, _state.section.contents as Pen[], devicePortsUsed)) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 0, errorText: "Duplicate ports" } }),
          handleTimeout(0)
        )
      }

      return dispatch({ currentSectionLevel: _state.currentSectionLevel + 1 })
     }

    // Save Section    

    let NewLfTerminalLinks: CreateTerminalLink[] | undefined = undefined; 
    if (systemType === SystemTypes.freedaLf) {
      NewLfTerminalLinks = lfExtractTerminalLinks(_state.section);
    }
    
    setLoading(true)
    const response = await createSectionLocations(connection.connection.domain, _state.section.name, Number(_state.section.columns), systemId, newSectionData, _state.systemType === SystemTypes.freedaLf ? NewLfTerminalLinks : undefined)
      if (!response.success) {
        dispatch({ currentSectionLevel: 3, error: { Elementid: 3, errorText: response.message } });
        handleTimeout(3);
        setLoading(false);
        return
      }
    setLoading(false);
    closeModal();
  }

  // Function to handle "Go Back"
  const goBack = () => {
    if (_state.currentSectionLevel > 1) dispatch({currentSectionLevel:_state.currentSectionLevel-1})
  };

  return (
    <ModalContext.Provider value={{state:_state, dispatch}}>
      <NewModal
        modalState={true}
        overrides={{
          mainContentPadding: MainContentPaddingTypes.secondary,
          headerContentHeight: "65px",
          secondaryContentScroll: true
        }}
        predefinedButtons={{
          onClose: closeModal,
          onGoBack: _state.currentSectionLevel > 1 ? goBack : undefined
        }}
        header={t("locationFeederPages.sections.newSection", { defaultValue: "new section" })}
        headerContent={
          <ProgressSteps
            titles={[t("devices.layout", { defaultValue: "layout" }), t("devices.ioBoards", { defaultValue: "i/o boards" }), t("buttons.apply", { defaultValue: "apply" })]}
            icons={[glyphs.Animal({}), glyphs.Animal({}), glyphs.Animal({})]}
            opacityValues={_state.currentSectionLevel === 1 ? [1, 0.5, 0.5] : _state.currentSectionLevel === 2 ? [0.5, 1, 0.5] : [0.5, 0.5, 1]}
          />
        }
        // secondaryContent={_state.currentSectionLevel === 3 ?
        //   <div style={{ width: mobileXs ? "290px" : "615px" }}>
        //     {_state.systemType === SystemTypes.freedaLf ? <NewSectionModalLvl3/> : _state.systemType === SystemTypes.freedaEsf_WalkThrough ? <NewEsfWfSectionModalLvl3/> : undefined}
        //   </div>
        //   : undefined
        // }
        mainContent={
          <div style={{ minWidth: mobileXs ? "290px" : "615px", height: "100%", overflow: "hidden"}}>
            {_state.currentSectionLevel  === 1 && (_state.systemType === SystemTypes.freedaLf ? <NewSectionModalLvl1/> : _state.systemType === SystemTypes.freedaEsf_WalkThrough ? <NewEsfWfSectionModalLvl1/> : undefined)}
            {_state.currentSectionLevel  === 2 && (_state.systemType === SystemTypes.freedaLf ? <NewSectionModalLvl2/> : _state.systemType === SystemTypes.freedaEsf_WalkThrough ? <NewEsfWfSectionModalLvl2/> : undefined)}
            {_state.currentSectionLevel === 3 && (_state.systemType === SystemTypes.freedaLf ? <NewSectionModalLvl3 /> : _state.systemType === SystemTypes.freedaEsf_WalkThrough ? <NewEsfWfSectionModalLvl3 /> : undefined)}
            {loading && <WaitAnimation color={colors.greyLightTertiary} />}
          </div>
        }
        footerContent={
          <NewButton 
            content={_state.currentSectionLevel < 3 ? t("buttons.next", {defaultValue: "next"}) : t("buttons.create", {defaultValue: "create"})}
            onClick={ handleSaveSection }
          />
        }
        footerMessage={_state.error.errorText}
        />
      </ModalContext.Provider>
  );
}

